import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicBoxComponent, MzicButtonComponent } from '@mzic/mzic-arpeggio-ui';
import { LanguageService, PosthogAnalyticsService } from '@mzic/mzic-services';
import { map } from 'rxjs';
import { ArtistCatalogComponent } from '../../components/artist-catalog/artist-catalog.component';
import { HeaderComponent } from '../../components/header/header.component';
import { InvestorPromoBannerComponent } from './investor-promo-banner.component';

// Interface para oportunidades de investimento
interface InvestmentOpportunity {
  image: string;
  title: string;
  artist: string;
  categoryKey: 'phonogram' | 'catalog' | 'composition';
  historicalGainsMin?: number; // Range mínimo (opcional)
  historicalGainsMax?: number; // Range máximo (opcional)
  historicalGains?: number; // Valor único quando não há range
  targetYieldMin?: number; // Rentabilidade alvo (range mínimo, opcional)
  targetYieldMax?: number; // Rentabilidade alvo (range máximo, opcional)
  targetYield?: number; // Rentabilidade alvo (valor único)
  url?: string; // URL opcional para roteamento
}

type OpportunityYieldKind = 'historical' | 'target';
type OpportunityYieldMode = 'single' | 'range' | 'minOnly' | 'maxOnly';

interface OpportunityYieldDisplay {
  kind: OpportunityYieldKind;
  mode: OpportunityYieldMode;
  value?: number;
  min?: number;
  max?: number;
}

interface OpportunityYieldI18nDisplay {
  kind: OpportunityYieldKind;
  labelKey: string;
  valueKey: string;
  params: Record<string, string>;
}

@Component({
  selector: 'mzic-investor-landing',
  imports: [
    CommonModule,
    HeaderComponent,
    TranslocoModule,
    RouterModule,
    MzicButtonComponent,
    MzicBoxComponent,
    ArtistCatalogComponent,
    InvestorPromoBannerComponent,
  ],
  templateUrl: './investor-landing.component.html',
  styleUrl: './investor-landing.component.scss',
  providers: [
    provideTranslocoScope({
      scope: 'investor-landing',
      alias: 'lang',
    }),
  ],
})
export class InvestorLandingComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private analyticsService = inject(PosthogAnalyticsService);
  private breakpointObserver = inject(BreakpointObserver);
  readonly showPromoBanner = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((params) => params.get('promo') === 'true'),
    ),
    { initialValue: false },
  );
  readonly isMobile = toSignal(
    this.breakpointObserver
      .observe('(max-width: 768px)')
      .pipe(map((state) => state.matches)),
    {
      initialValue:
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 768px)').matches,
    },
  );

  appStoreUrl = 'https://apps.apple.com/br/app/mzic/id1670495038';
  playStoreUrl = 'https://play.google.com/store/apps/details?id=io.mzic.app';

  // Social media links
  socialLinks = {
    instagram: 'https://www.instagram.com/mzicbrasil/',
    youtube: 'https://youtube.com/@mzicapp',
    spotify: 'https://spotify.com/mzic',
    linkedin: 'https://www.linkedin.com/company/mzic/',
  };

  // Footer links
  footerLinks = {
    forArtists: 'https://artists.mzic.io/',
    termsOfUse: 'https://legal.mzic.io/pt-br/terms-of-use',
    privacyPolicy: 'https://legal.mzic.io/pt-br/privacy',
    faqLearnMore: 'https://mzic.io/faq',
  };

  // App badges images
  googlePlayLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-google-play-icon.svg';
  appleLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-apple-icon.svg';
  googlePlayDownload = 'assets/images/googleplay-download.svg';
  appStoreDownload = 'assets/images/appstore-download.svg';

  // Partner logos
  transferoLogo = 'assets/images/transfero-logo.png';
  vbsoLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-vbso-icon.png';
  mercadoBitcoinLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-mercado-bitcoin-icon.svg';
  cvmLogo = 'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-cvm-icon.svg';

  explanationSteps = [0, 1, 2]; // Step indices for i18n
  // Music explanation SVG icons
  explanationSvgIcons = [
    'assets/images/disco-with-play-slide1.svg',
    'assets/images/coins-slide2.svg',
    'assets/images/notebook-profit-slide3.svg',
  ];

  qrCodeMzic =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-default-download-app-url.svg';

  activeFAQ: string | null = null;
  activePortfolioTab = 0;
  activeExplanationStep = 0;
  isChangingStep = false;
  isChangingPortfolioTab = false;

  portfolioTabs = [
    {
      icon: 'assets/icons/music-cd.svg',
      image: 'assets/images/portfolio-slide1.svg',
    },
    {
      icon: 'assets/icons/microphone2.svg',
      image: 'assets/images/portfolio-slide2.svg',
    },
    {
      icon: 'assets/icons/radio.svg',
      image: 'assets/images/portfolio-slide3.svg',
    },
  ];

  // Artist catalog data for the portfolio tabs
  artistCatalogData = [
    {
      displayMode: 'catalog' as const,
      artistImage:
        'https://mzic-website.s3.us-east-1.amazonaws.com/sofiaMarin.png',
      artistImageLink: '#',
      artistName: 'Sofia Marin',
      items: [
        {
          id: '1',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/linha-d-agua-small.svg',
          title: "Linha D'Água",
          subtitle: 'Sofia Marin',
        },
        {
          id: '2',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/loop-infinito-small.svg',
          title: 'Loop Infinito',
          subtitle: 'Sofia Marin',
        },
        {
          id: '3',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/luz-miuda-small.svg',
          title: 'Luz Miúda',
          subtitle: 'Sofia Marin',
        },
        {
          id: '4',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/carta-pra-ninguem-small.svg',
          title: 'Carta Para Ninguém',
          subtitle: 'Sofia Marin',
        },
        {
          id: '5',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/te-deixei-no-silencio-small.svg',
          title: 'Te Deixei no Silêncio',
          subtitle: 'Sofia Marin',
        },
      ],
    },
    {
      displayMode: 'image' as const,
      artistImage:
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-live-shows.png',
      artistImageLink: '#',
      artistName: '',
      items: [],
    },
    {
      displayMode: 'image' as const,
      artistImage:
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-sofia-marin-catalogue.svg',
      artistImageLink: '#',
      artistName: '',
      items: [],
    },
  ];

  opportunities: InvestmentOpportunity[] = [
    {
      image: 'assets/images/portfolio-slide5.png',
      title: 'Aproveita - Ao Vivo',
      artist: 'Clayton & Romário',
      categoryKey: 'composition',
      targetYield: 24,
      url: 'https://mzic.app.link/test',
    },
    {
      image: 'assets/images/portfolio-slide2.png',
      title: 'Manda um Oi',
      artist: 'Guilherme e Benuto, Simone Mendes',
      categoryKey: 'composition',
      historicalGainsMin: 20.72,
      historicalGainsMax: 23.47,
      url: 'https://mzic.app.link/test',
    },
    {
      image: 'assets/images/portfolio-slide3.png',
      title: 'Xonei',
      artist: 'Jorge & Mateus, Henrique & Juliano',
      categoryKey: 'composition',
      historicalGains: 24.2,
      url: 'https://mzic.app.link/test',
    },
    {
      image: 'assets/images/portfolio-slide4.png',
      title: 'Grau de Maluca',
      artist: 'Guilherme e Benuto, Grupo Menos É Mais, Matheus Fernandes',
      categoryKey: 'composition',
      targetYield: 25,
      url: 'https://mzic.app.link/test',
    },
    {
      image: 'assets/images/portfolio-slide1.png',
      title: 'DEUS É MUITO BOM',
      artist: 'Luan Santana',
      categoryKey: 'composition',
      historicalGainsMin: 15.85,
      historicalGainsMax: 17.29,
      url: 'https://mzic.app.link/test',
    },
  ];

  // Scroll sync properties
  private carouselScrollOffset = 0;
  private rafId: number | null = null;
  private lastScrollProgress = -1;

  // Section fade animation properties
  private sectionObserver: IntersectionObserver | null = null;
  private fadeableSections: string[] = [
    'hero-section',
    'broker-section',
    'investment-opportunities-section',
    'music-explanation-section',
    'portfolio-section',
    'app-download-section',
    'faq-section',
    'partners-section',
  ];

  // Swipe properties for explanation steps
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private isSwiping = false;
  private swipeDebounceTimeout: number | null = null;
  private swipeInitialized = false;
  private swipeRetryCount = 0;
  private swipeRetryTimeouts: number[] = [];
  private readonly MAX_SWIPE_INIT_RETRIES = 5;

  // Mathematical constants for swipe detection
  private readonly SWIPE_MIN_DISTANCE = 50; // Minimum pixels to consider as swipe
  private readonly SWIPE_MAX_TIME = 300; // Maximum milliseconds for swipe gesture
  private readonly SWIPE_MIN_VELOCITY = 0.1; // Minimum velocity (pixels/ms)
  private readonly SWIPE_ANGLE_THRESHOLD = 30; // Maximum angle deviation from horizontal (degrees)
  private readonly SWIPE_DEBOUNCE_TIME = 150; // Debounce time between swipes (ms)

  // Track FAQ and Partners sections for synchronized fade
  private faqSection: HTMLElement | null = null;
  private partnersSection: HTMLElement | null = null;
  private syncedSectionsState = {
    faqVisible: false,
    partnersVisible: false,
    lastGroupedVisibility: false,
  };
  private wasPromoBannerVisible = false;

  constructor() {
    effect(() => {
      const isPromoBannerVisible = this.showPromoBanner();
      const language = this.languageService.languageState();

      if (
        isPromoBannerVisible &&
        language !== '' &&
        !this.wasPromoBannerVisible
      ) {
        this.analyticsService.captureWithContext(
          'investor_landing_banner_promo_impression',
          {
            section: 'promo_banner',
            language,
            promo: true,
          },
        );
      }

      this.wasPromoBannerVisible = isPromoBannerVisible;
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();
    this.preloadImages();

    // Track page view with PostHog
    this.analyticsService.trackPageView('investor_landing', {
      language: this.languageService.languageState(),
    });
  }

  ngAfterViewInit() {
    this.initializeSectionFadeAnimation();

    // Initialize swipe with retry mechanism for better reliability
    this.initializeSwipeWithRetry();
  }

  /**
   * Preload all portfolio images to prevent loading delays when switching tabs
   * This improves UX by ensuring images are cached before user interaction
   */
  private preloadImages() {
    // Preload artist catalog images
    this.artistCatalogData.forEach((catalogData) => {
      const img = new Image();
      img.src = catalogData.artistImage;

      // Preload catalog item images if in catalog mode
      if (
        catalogData.displayMode === 'catalog' &&
        catalogData.items.length > 0
      ) {
        catalogData.items.forEach((item) => {
          const itemImg = new Image();
          itemImg.src = item.image;
        });
      }
    });

    // Preload portfolio tab images (for backward compatibility if needed)
    this.portfolioTabs.forEach((tab) => {
      const img = new Image();
      img.src = tab.image;
    });

    // Preload opportunity images
    this.opportunities.forEach((opportunity) => {
      const img = new Image();
      img.src = opportunity.image;
    });

    // Preload explanation SVG icons
    this.explanationSvgIcons.forEach((icon) => {
      const img = new Image();
      img.src = icon;
    });
  }

  ngOnDestroy() {
    // Cancel any pending animation frames for smooth performance
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Clear swipe debounce timeout
    if (this.swipeDebounceTimeout) {
      clearTimeout(this.swipeDebounceTimeout);
      this.swipeDebounceTimeout = null;
    }

    // Clear all retry timeouts
    this.swipeRetryTimeouts.forEach((id) => clearTimeout(id));
    this.swipeRetryTimeouts = [];

    // Clean up section observer
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }

    // Clean up swipe listeners
    if (this.swipeInitialized) {
      const targets = [
        '.music-explanation-section',
        '.explanation-content',
        '.explanation-image',
      ];

      for (const selector of targets) {
        const element = document.querySelector(selector);
        if (element) {
          this.removeSwipeListeners(element);
          break;
        }
      }
    }

    // Clear all analytics timers
    this.analyticsService.clearAllTimers();
  }

  setLanguageFromRoute() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const lang = params['lang'];
        if (lang) {
          this.translocoService.setActiveLang(lang);
          this.languageService.languageState.set(lang);
        }
      });
  }

  toggleFAQ(faqId: string) {
    const action = this.activeFAQ === faqId ? 'close' : 'open';

    // Track FAQ toggle
    this.analyticsService.captureWithContext('investor_landing_accordion_faq', {
      faq_id: faqId,
      action: action,
      section: 'faq',
      language: this.languageService.languageState(),
    });

    if (this.activeFAQ === faqId) {
      this.activeFAQ = null;
    } else {
      this.activeFAQ = faqId;
    }
  }

  goToHeroCta() {
    // Track hero CTA button click
    this.analyticsService.captureWithContext(
      'investor_landing_button_hero_cta',
      {
        section: 'hero',
        cta_text: 'Download App',
        language: this.languageService.languageState(),
      },
    );

    this.scrollToAppDownloadSection();
  }

  scrollToAppDownloadSection() {
    const appDownloadSection = document.querySelector(
      '.app-download-section',
    ) as HTMLElement;

    if (!appDownloadSection) {
      console.warn('app-download-section not found');
      return;
    }

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;

    // Get section position and dimensions
    const sectionRect = appDownloadSection.getBoundingClientRect();
    const sectionHeight = sectionRect.height;

    // Mathematical calculation to center the section in viewport
    // Current section top position relative to document
    const currentScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;
    const sectionTopFromDocument = sectionRect.top + currentScrollTop;

    // Calculate target scroll position to center the section
    // Formula: section_top - (viewport_height - section_height) / 2
    const targetScrollTop =
      sectionTopFromDocument - (viewportHeight - sectionHeight) / 2;

    // Ensure we don't scroll past the document bounds
    const maxScrollTop = document.documentElement.scrollHeight - viewportHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

    // Smooth scroll with enhanced easing for better UX
    window.scrollTo({
      top: finalScrollTop,
      behavior: 'smooth',
    });
  }

  goToAboutMzic() {
    // Track navigation to About page
    this.analyticsService.captureWithContext(
      'investor_landing_navigation_about',
      {
        section: 'navigation',
        destination: 'about',
        language: this.languageService.languageState(),
      },
    );

    const lang = this.languageService.languageState();
    if (lang !== '') {
      this.router.navigate([`${lang}/about`]);
    } else {
      this.router.navigate(['/about']);
    }
  }

  goToContact() {
    // Track navigation to Contact page
    this.analyticsService.captureWithContext(
      'investor_landing_navigation_contact',
      {
        section: 'navigation',
        destination: 'contact',
        language: this.languageService.languageState(),
      },
    );

    const lang = this.languageService.languageState();
    if (lang !== '') {
      this.router.navigate([`${lang}/contact`]);
    } else {
      this.router.navigate(['/contact']);
    }
  }

  onOpportunityClick(opportunity: InvestmentOpportunity) {
    // Track opportunity click
    const yieldDisplay = this.getOpportunityYieldDisplay(opportunity);

    const analyticsPayload: Record<string, unknown> = {
      opportunity_title: opportunity.title,
      artist_name: opportunity.artist,
      category: opportunity.categoryKey,
      has_url: !!opportunity.url,
      section: 'investment_opportunities',
      language: this.languageService.languageState(),
    };

    if (yieldDisplay) {
      const yieldAnalyticsDisplay =
        this.getOpportunityYieldAnalyticsDisplay(yieldDisplay);

      analyticsPayload['yield_kind'] = yieldDisplay.kind;
      analyticsPayload['yield_mode'] = yieldDisplay.mode;
      analyticsPayload['yield_display'] = yieldAnalyticsDisplay;

      if (yieldDisplay.kind === 'historical') {
        analyticsPayload['historical_gains_range'] = yieldAnalyticsDisplay;
      } else {
        analyticsPayload['target_yield_range'] = yieldAnalyticsDisplay;
      }
    }

    this.analyticsService.captureWithContext(
      'investor_landing_click_opportunity',
      analyticsPayload,
    );

    if (opportunity.url) {
      window.open(opportunity.url, '_blank');
    }
  }

  setActivePortfolioTab(index: number) {
    if (this.activePortfolioTab !== index) {
      const previousTab = this.activePortfolioTab;
      const timeOnPreviousTab = this.analyticsService.getTimeOnSection(
        `portfolio_tab_${previousTab}`,
      );

      // Track portfolio tab change
      this.analyticsService.captureWithContext(
        'investor_landing_tab_portfolio',
        {
          tab_index: index,
          tab_from: previousTab,
          tab_to: index,
          time_on_previous_tab: timeOnPreviousTab,
          section: 'portfolio',
          language: this.languageService.languageState(),
        },
      );

      // Start timer for new tab
      this.analyticsService.startSectionTimer(`portfolio_tab_${index}`);

      this.isChangingPortfolioTab = true;
      // Give time for the fade out to complete before changing the image
      setTimeout(() => {
        this.activePortfolioTab = index;
        // Reset the changing state after the new image starts loading
        setTimeout(() => {
          this.isChangingPortfolioTab = false;
        }, 50);
      }, 250);
    }
  }

  setActiveExplanationStep(
    index: number,
    navigationMethod: 'button' | 'swipe' = 'button',
  ) {
    if (this.activeExplanationStep !== index) {
      const previousStep = this.activeExplanationStep;
      const timeOnPreviousStep = this.analyticsService.getTimeOnSection(
        `explanation_step_${previousStep}`,
      );

      // Track explanation step change
      this.analyticsService.captureWithContext(
        'investor_landing_step_explanation',
        {
          step_index: index,
          step_from: previousStep,
          step_to: index,
          navigation_method: navigationMethod,
          time_on_previous_step: timeOnPreviousStep,
          section: 'music_explanation',
          language: this.languageService.languageState(),
        },
      );

      // Start timer for new step
      this.analyticsService.startSectionTimer(`explanation_step_${index}`);

      this.isChangingStep = true;
      setTimeout(() => {
        this.activeExplanationStep = index;
        setTimeout(() => {
          this.isChangingStep = false;
        }, 50);
      }, 250);
    }
  }

  onImageLoad() {
    this.isChangingStep = false;
  }

  onPortfolioImageLoad() {
    // Only reset the changing state if we're currently changing
    if (this.isChangingPortfolioTab) {
      this.isChangingPortfolioTab = false;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Cancel previous animation frame if still pending
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    // Schedule smooth scroll update
    this.rafId = requestAnimationFrame(() => {
      this.syncCarouselScroll();
    });
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    if (window.innerWidth <= 768) {
      const carouselTrack = document.querySelector(
        '.carousel-track',
      ) as HTMLElement;
      if (carouselTrack) {
        carouselTrack.style.transform = 'translate3d(0, 0, 0)';
        this.carouselScrollOffset = 0;
        this.lastScrollProgress = -1;
      }
    }
  }

  private syncCarouselScroll() {
    // Skip auto-scroll on mobile - use native horizontal scrolling instead
    if (window.innerWidth <= 768) {
      this.rafId = null;
      return;
    }

    const carouselTrack = document.querySelector(
      '.carousel-track',
    ) as HTMLElement;

    if (!carouselTrack) return;

    // Get carousel track dimensions and position
    const trackRect = carouselTrack.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate how much of the carousel-track is visible
    const trackTop = trackRect.top;
    const trackBottom = trackRect.bottom;
    const trackHeight = trackRect.height;

    // Check if carousel track is in viewport
    const isInViewport = trackTop < viewportHeight && trackBottom > 0;

    if (!isInViewport) {
      this.rafId = null;
      return;
    }

    // Calculate visible portion of the track
    const visibleTop = Math.max(0, trackTop);
    const visibleBottom = Math.min(viewportHeight, trackBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibilityPercentage = visibleHeight / trackHeight;

    // Start scrolling when 70% or more is visible (allows first image to show)
    if (visibilityPercentage < 0.7) {
      this.rafId = null;
      return;
    }

    // Enhanced mathematical scroll calculation for optimal last image visibility
    // Adapts to different screen sizes to ensure last image is always fully visible

    // Calculate card dimensions for different screen sizes
    const isDesktop = viewportHeight >= 768;
    const cardWidth = isDesktop ? 420 : 280; // Desktop vs Mobile card width
    const cardGap = isDesktop ? 40 : 20; // Gap between cards (2.5em = ~40px desktop, 20px mobile)

    // Calculate total scrollable distance needed to show last image completely
    const carouselWidth = carouselTrack.scrollWidth;
    const visibleWidth = carouselTrack.parentElement?.offsetWidth || 0;
    const maxScrollDistance = Math.max(0, carouselWidth - visibleWidth);

    // Calculate minimum scroll range needed to show last image
    // Last image needs to be fully visible = scroll distance + one card width
    const lastImageBuffer = cardWidth + cardGap; // Buffer to show last image completely
    const optimalScrollRange = maxScrollDistance + lastImageBuffer;

    // Responsive scroll range calculation:
    // Desktop: Larger range for smoother experience
    // Mobile: Smaller range for faster navigation
    const baseScrollRange = isDesktop
      ? viewportHeight * 1.8
      : viewportHeight * 1.4;
    const scrollRange = Math.max(baseScrollRange, optimalScrollRange);

    // Define scroll boundaries with different triggers for desktop and mobile
    // Desktop: 70% visibility (30% hidden) - PERFECT, don't change
    // Mobile: 90% visibility (10% hidden) - Start earlier for better user experience
    const visibilityTrigger = isDesktop
      ? trackHeight * 0.3 // Desktop: Start when 30% hidden (70% visible)
      : trackHeight * 0.1; // Mobile: Start when 10% hidden (90% visible)
    const scrollStart = viewportHeight - visibilityTrigger;

    // Calculate progress (0 to 1) ensuring last image is fully visible
    const rawProgress = (scrollStart - trackTop) / scrollRange;
    const scrollProgress = Math.max(0, Math.min(1, rawProgress));

    // Track progress for optimization

    // Mathematical guarantees:
    // 1. Starts at 70% visibility (first image partially visible)
    // 2. Extended range ensures last image is completely visible
    // 3. Adapts to screen size for optimal experience
    // 4. Maintains bidirectional consistency

    // Skip update if progress hasn't changed significantly (optimize performance)
    const progressDiff = Math.abs(scrollProgress - this.lastScrollProgress);
    if (progressDiff < 0.0001) {
      this.rafId = null;
      return;
    }

    this.lastScrollProgress = scrollProgress;

    // Only scroll if there's actual distance to scroll
    if (maxScrollDistance <= 0) {
      this.rafId = null;
      return;
    }

    // Calculate final carousel offset with enhanced range for last image visibility
    // Scale progress to ensure the last image is fully visible within our scroll range
    const scaledProgress = Math.min(
      1,
      scrollProgress * (optimalScrollRange / maxScrollDistance),
    );

    // Apply smooth easing while maintaining mathematical precision
    const easedProgress = this.easeInOutQuart(scaledProgress);
    this.carouselScrollOffset = easedProgress * maxScrollDistance;

    // Apply transformation with subpixel precision
    carouselTrack.style.willChange = 'transform';
    carouselTrack.style.transform = `translate3d(-${this.carouselScrollOffset.toFixed(2)}px, 0, 0)`;

    // Reset RAF id
    this.rafId = null;
  }

  private easeInOutQuart(t: number): number {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
  }

  private initializeSectionFadeAnimation() {
    // Mathematical calculation for optimal fade thresholds
    // Desktop: More precise control, smoother transitions
    // Mobile: Faster transitions, less resource intensive
    const isMobile = window.innerWidth <= 768;
    const viewportHeight = window.innerHeight;

    // Calculate intersection thresholds mathematically
    // Desktop: More granular steps for smoother animation
    // Mobile: Fewer steps for better performance
    const thresholdSteps = isMobile ? 5 : 10;
    const thresholds = Array.from(
      { length: thresholdSteps },
      (_, i) => i / (thresholdSteps - 1),
    );

    // Mathematical root margin calculation
    // Creates a buffer zone based on viewport height
    // Desktop: Larger buffer for smoother experience
    // Mobile: Smaller buffer to avoid early triggering
    const rootMarginPercent = isMobile ? 6.75 : 4; // Percentage of viewport height (reduced by 20% for earlier fade-in)
    const rootMarginPx = Math.round(viewportHeight * (rootMarginPercent / 100));

    // Create observer with calculated mathematical parameters
    this.sectionObserver = new IntersectionObserver(
      (entries) => this.handleSectionIntersection(entries),
      {
        root: null, // Use viewport as root
        rootMargin: `-${rootMarginPx}px 0px -${rootMarginPx}px 0px`, // Symmetric margins
        threshold: thresholds, // Granular thresholds for smooth animation
      },
    );

    // Initialize sections with fade-out state
    // Add observer to each fadeable section
    setTimeout(() => {
      this.fadeableSections.forEach((sectionClass) => {
        const section = document.querySelector(
          `.${sectionClass}`,
        ) as HTMLElement;
        if (section) {
          // Initialize with fade-out state
          section.classList.add('section-fade-out');
          section.classList.remove('section-fade-in');

          // Observe the section
          this.sectionObserver?.observe(section);
        }
      });
    }, 100); // Small delay to ensure DOM is ready
  }

  private handleSectionIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      const section = entry.target as HTMLElement;
      const isFaqSection = section.classList.contains('faq-section');
      const isPartnersSection = section.classList.contains('partners-section');

      // Mathematical fade calculation based on intersection ratio
      const intersectionRatio = entry.intersectionRatio;

      // Calculate fade threshold mathematically
      const isMobile = window.innerWidth <= 768;
      const fadeInThreshold = isMobile ? 0.25 : 0.3;
      const fadeOutThreshold = isMobile ? 0.15 : 0.2;

      // Track section visibility for analytics
      const sectionClasses = Array.from(section.classList);
      const sectionName =
        sectionClasses
          .find((cls) => cls.endsWith('-section'))
          ?.replace('-section', '') || 'unknown';

      if (intersectionRatio >= fadeInThreshold) {
        this.analyticsService.startSectionTimer(sectionName);
      } else if (intersectionRatio <= fadeOutThreshold) {
        this.analyticsService.stopSectionTimer(sectionName);
      }

      // Special handling for FAQ and Partners sections - synchronized fade
      if (isFaqSection || isPartnersSection) {
        this.handleGroupedSectionsFade(
          section,
          intersectionRatio,
          fadeInThreshold,
          fadeOutThreshold,
          isFaqSection,
        );
      } else {
        // Standard fade behavior for other sections
        if (intersectionRatio >= fadeInThreshold) {
          section.classList.add('section-fade-in');
          section.classList.remove('section-fade-out');
        } else if (intersectionRatio <= fadeOutThreshold) {
          section.classList.add('section-fade-out');
          section.classList.remove('section-fade-in');
        }
      }

      // Set CSS custom property for advanced animations
      section.style.setProperty(
        '--intersection-ratio',
        intersectionRatio.toString(),
      );
    });
  }

  private handleGroupedSectionsFade(
    section: HTMLElement,
    intersectionRatio: number,
    fadeInThreshold: number,
    fadeOutThreshold: number,
    isFaqSection: boolean,
  ) {
    // Update visibility state for the specific section
    if (isFaqSection) {
      this.syncedSectionsState.faqVisible =
        intersectionRatio >= fadeInThreshold;
      this.faqSection = section;
    } else {
      this.syncedSectionsState.partnersVisible =
        intersectionRatio >= fadeInThreshold;
      this.partnersSection = section;
    }

    // Mathematical calculation for grouped visibility:
    // Both sections fade together when either one reaches the threshold
    // This creates a visual grouping effect
    const groupedVisibility = this.calculateGroupedVisibility(
      this.syncedSectionsState.faqVisible,
      this.syncedSectionsState.partnersVisible,
      intersectionRatio,
      fadeInThreshold,
      fadeOutThreshold,
    );

    // Apply synchronized fade to both sections if they exist
    if (this.faqSection && this.partnersSection) {
      const shouldFadeIn = groupedVisibility;
      const shouldFadeOut = !groupedVisibility;

      // Apply fade state to both sections simultaneously
      [this.faqSection, this.partnersSection].forEach((targetSection) => {
        if (shouldFadeIn) {
          targetSection.classList.add('section-fade-in');
          targetSection.classList.remove('section-fade-out');
        } else if (shouldFadeOut) {
          targetSection.classList.add('section-fade-out');
          targetSection.classList.remove('section-fade-in');
        }
      });

      this.syncedSectionsState.lastGroupedVisibility = groupedVisibility;
    } else {
      // If partner section not yet available, handle individually
      if (intersectionRatio >= fadeInThreshold) {
        section.classList.add('section-fade-in');
        section.classList.remove('section-fade-out');
      } else if (intersectionRatio <= fadeOutThreshold) {
        section.classList.add('section-fade-out');
        section.classList.remove('section-fade-in');
      }
    }
  }

  private calculateGroupedVisibility(
    faqVisible: boolean,
    partnersVisible: boolean,
    currentRatio: number,
    fadeInThreshold: number,
    fadeOutThreshold: number,
  ): boolean {
    // Mathematical formula for grouped visibility:
    // Fade in when EITHER section reaches fadeInThreshold
    // Fade out only when BOTH sections are below fadeOutThreshold
    // This creates a "sticky" grouping effect

    const eitherVisible = faqVisible || partnersVisible;
    const bothInvisible = !faqVisible && !partnersVisible;

    // Hysteresis to prevent flickering:
    // Use different thresholds for fade in vs fade out
    if (this.syncedSectionsState.lastGroupedVisibility) {
      // Currently visible - require both to be invisible to fade out
      return !bothInvisible || currentRatio > fadeOutThreshold;
    } else {
      // Currently hidden - require either to be visible to fade in
      return eitherVisible && currentRatio >= fadeInThreshold;
    }
  }

  // Initialize swipe with retry mechanism for better reliability
  private initializeSwipeWithRetry() {
    // Only enable swipe on mobile devices
    if (window.innerWidth > 768) {
      this.swipeInitialized = true;
      return;
    }

    // Use requestAnimationFrame to ensure DOM is painted
    requestAnimationFrame(() => {
      const success = this.tryInitializeSwipe();

      if (!success && this.swipeRetryCount < this.MAX_SWIPE_INIT_RETRIES) {
        this.swipeRetryCount++;
        // Exponential backoff: 100ms, 200ms, 400ms, 800ms, 1600ms
        const delay = 100 * Math.pow(2, this.swipeRetryCount - 1);
        const timeoutId = window.setTimeout(
          () => this.initializeSwipeWithRetry(),
          delay,
        );
        this.swipeRetryTimeouts.push(timeoutId);
      }
    });
  }

  // Try to initialize swipe gestures
  private tryInitializeSwipe(): boolean {
    // If already initialized, don't reinitialize
    if (this.swipeInitialized) return true;

    // Try multiple selectors to find the target area
    const targets = [
      '.music-explanation-section',
      '.explanation-content',
      '.explanation-image',
    ];

    let swipeTarget: Element | null = null;
    for (const selector of targets) {
      swipeTarget = document.querySelector(selector);
      if (swipeTarget) break;
    }

    if (!swipeTarget) {
      console.debug(
        `Swipe init attempt ${this.swipeRetryCount + 1}: Target not found yet`,
      );
      return false;
    }

    // Verify element has dimensions (is rendered)
    const rect = swipeTarget.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.debug(
        `Swipe init attempt ${this.swipeRetryCount + 1}: Target has no dimensions yet`,
      );
      return false;
    }

    // Remove any existing listeners first to prevent duplicates
    this.removeSwipeListeners(swipeTarget);

    // Add touch event listeners with passive option for better performance
    swipeTarget.addEventListener('touchstart', this.handleTouchStart, {
      passive: false,
    });
    swipeTarget.addEventListener('touchmove', this.handleTouchMove, {
      passive: false,
    });
    swipeTarget.addEventListener('touchend', this.handleTouchEnd, {
      passive: true,
    });

    this.swipeInitialized = true;
    console.debug('Swipe gestures initialized successfully');
    return true;
  }

  // Remove swipe listeners (for cleanup)
  private removeSwipeListeners(element: Element) {
    element.removeEventListener('touchstart', this.handleTouchStart);
    element.removeEventListener('touchmove', this.handleTouchMove);
    element.removeEventListener('touchend', this.handleTouchEnd);
  }

  // Bound event handlers to preserve 'this' context
  private handleTouchStart = (e: Event) => this.onTouchStart(e as TouchEvent);
  private handleTouchMove = (e: Event) => this.onTouchMove(e as TouchEvent);
  private handleTouchEnd = (e: Event) => this.onTouchEnd(e as TouchEvent);

  private onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) return; // Only handle single touch

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isSwiping = false;
  }

  private onTouchMove(event: TouchEvent) {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);

    // Check if this is likely a horizontal swipe
    if (deltaX > deltaY && deltaX > 10) {
      this.isSwiping = true;
      // Prevent vertical scrolling during horizontal swipe
      event.preventDefault();
    }
  }

  private onTouchEnd(event: TouchEvent) {
    if (!this.isSwiping || event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];
    const endTime = Date.now();

    // Calculate swipe metrics mathematically
    const swipeData = this.calculateSwipeMetrics(
      this.touchStartX,
      this.touchStartY,
      touch.clientX,
      touch.clientY,
      this.touchStartTime,
      endTime,
    );

    // Validate and process swipe
    if (this.isValidSwipe(swipeData)) {
      this.processSwipe(swipeData.direction as 'left' | 'right');
    }

    // Reset swipe state
    this.isSwiping = false;
  }

  private calculateSwipeMetrics(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    startTime: number,
    endTime: number,
  ) {
    // Calculate distance and direction vectors
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const time = endTime - startTime;
    const velocity = distance / time;

    // Calculate angle in degrees (0° = horizontal right, 90° = vertical up)
    const angle = Math.abs(
      (Math.atan2(deltaY, Math.abs(deltaX)) * 180) / Math.PI,
    );

    // Determine horizontal direction
    const direction = deltaX > 0 ? 'right' : 'left';

    return {
      deltaX: Math.abs(deltaX),
      deltaY: Math.abs(deltaY),
      distance,
      time,
      velocity,
      angle,
      direction,
    };
  }

  private isValidSwipe(swipeData: {
    deltaX: number;
    deltaY: number;
    distance: number;
    time: number;
    velocity: number;
    angle: number;
    direction: string;
  }): boolean {
    // Mathematical validation of swipe gesture
    return (
      swipeData.deltaX >= this.SWIPE_MIN_DISTANCE && // Minimum horizontal distance
      swipeData.time <= this.SWIPE_MAX_TIME && // Within time threshold
      swipeData.velocity >= this.SWIPE_MIN_VELOCITY && // Minimum velocity
      swipeData.angle <= this.SWIPE_ANGLE_THRESHOLD && // Primarily horizontal
      swipeData.deltaX > swipeData.deltaY // More horizontal than vertical
    );
  }

  private processSwipe(direction: 'left' | 'right') {
    // Prevent multiple rapid swipes with debounce
    if (this.swipeDebounceTimeout) return;

    // Calculate next step based on swipe direction
    let nextStep = this.activeExplanationStep;

    if (direction === 'left' && nextStep < this.explanationSteps.length - 1) {
      nextStep++;
    } else if (direction === 'right' && nextStep > 0) {
      nextStep--;
    } else {
      return; // No valid step change
    }

    // Apply the step change with swipe navigation method
    this.setActiveExplanationStep(nextStep, 'swipe');

    // Set debounce timeout to prevent rapid consecutive swipes
    this.swipeDebounceTimeout = window.setTimeout(() => {
      this.swipeDebounceTimeout = null;
    }, this.SWIPE_DEBOUNCE_TIME) as unknown as number;
  }

  /**
   * Track and open App Store link
   */
  openAppStore() {
    this.analyticsService.captureWithContext('investor_landing_link_appstore', {
      link_type: 'app_store',
      destination_url: this.appStoreUrl,
      section: 'app_download',
      language: this.languageService.languageState(),
    });

    this.openExternalUrl(this.appStoreUrl);
  }

  /**
   * Track and open Play Store link
   */
  openPlayStore() {
    this.analyticsService.captureWithContext(
      'investor_landing_link_playstore',
      {
        link_type: 'play_store',
        destination_url: this.playStoreUrl,
        section: 'app_download',
        language: this.languageService.languageState(),
      },
    );

    this.openExternalUrl(this.playStoreUrl);
  }

  /**
   * Track and open social media link
   */
  openSocialLink(platform: 'instagram' | 'youtube' | 'spotify' | 'linkedin') {
    const url = this.socialLinks[platform];

    this.analyticsService.captureWithContext('investor_landing_link_social', {
      link_type: 'social_media',
      platform: platform,
      destination_url: url,
      section: 'footer',
      language: this.languageService.languageState(),
    });

    window.open(url, '_blank');
  }

  /**
   * Track and open footer link
   */
  openFooterLink(
    linkType: 'forArtists' | 'termsOfUse' | 'privacyPolicy' | 'faqLearnMore',
  ) {
    const url = this.footerLinks[linkType];

    this.analyticsService.captureWithContext('investor_landing_link_footer', {
      link_type: linkType,
      destination_url: url,
      section: 'footer',
      language: this.languageService.languageState(),
    });

    window.open(url, '_blank');
  }

  /**
   * Formats a number to Portuguese locale with comma as decimal separator
   * Only displays decimals if the value has decimal places
   * @param value - The number to format
   * @returns Formatted string with comma as decimal separator (only when needed)
   */
  formatPercentage(value: number): string {
    const hasDecimals = value % 1 !== 0;
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }

  getOpportunityYieldI18nDisplay(
    opportunity: InvestmentOpportunity,
  ): OpportunityYieldI18nDisplay | null {
    const yieldDisplay = this.getOpportunityYieldDisplay(opportunity);
    if (!yieldDisplay) {
      return null;
    }

    const labelKey =
      yieldDisplay.kind === 'historical'
        ? 'lang.investmentOpportunities.historicalGainsLabel'
        : 'lang.investmentOpportunities.targetYieldLabel';

    const valueKey = this.getOpportunityYieldValueKey(yieldDisplay);

    return {
      kind: yieldDisplay.kind,
      labelKey,
      valueKey,
      params: this.getOpportunityYieldI18nParams(yieldDisplay),
    };
  }

  getOpportunityYieldDisplay(
    opportunity: InvestmentOpportunity,
  ): OpportunityYieldDisplay | null {
    const historical = this.getHistoricalYieldDisplay(opportunity);
    if (historical) {
      return historical;
    }

    return this.getTargetYieldDisplay(opportunity);
  }

  private getHistoricalYieldDisplay(
    opportunity: InvestmentOpportunity,
  ): OpportunityYieldDisplay | null {
    const hasSingle = opportunity.historicalGains !== undefined;
    const hasMin = opportunity.historicalGainsMin !== undefined;
    const hasMax = opportunity.historicalGainsMax !== undefined;

    if (hasSingle) {
      return {
        kind: 'historical',
        mode: 'single',
        value: opportunity.historicalGains,
      };
    }

    if (hasMin && hasMax) {
      return {
        kind: 'historical',
        mode: 'range',
        min: opportunity.historicalGainsMin,
        max: opportunity.historicalGainsMax,
      };
    }

    if (hasMin) {
      return {
        kind: 'historical',
        mode: 'minOnly',
        min: opportunity.historicalGainsMin,
      };
    }

    if (hasMax) {
      return {
        kind: 'historical',
        mode: 'maxOnly',
        max: opportunity.historicalGainsMax,
      };
    }

    return null;
  }

  private getTargetYieldDisplay(
    opportunity: InvestmentOpportunity,
  ): OpportunityYieldDisplay | null {
    const hasSingle = opportunity.targetYield !== undefined;
    const hasMin = opportunity.targetYieldMin !== undefined;
    const hasMax = opportunity.targetYieldMax !== undefined;

    if (hasSingle) {
      return {
        kind: 'target',
        mode: 'single',
        value: opportunity.targetYield,
      };
    }

    if (hasMin && hasMax) {
      return {
        kind: 'target',
        mode: 'range',
        min: opportunity.targetYieldMin,
        max: opportunity.targetYieldMax,
      };
    }

    if (hasMin) {
      return {
        kind: 'target',
        mode: 'minOnly',
        min: opportunity.targetYieldMin,
      };
    }

    if (hasMax) {
      return {
        kind: 'target',
        mode: 'maxOnly',
        max: opportunity.targetYieldMax,
      };
    }

    return null;
  }

  private getOpportunityYieldValueKey(
    yieldDisplay: OpportunityYieldDisplay,
  ): string {
    if (yieldDisplay.kind === 'historical') {
      switch (yieldDisplay.mode) {
        case 'single':
          return 'lang.investmentOpportunities.historicalGainsSingle';
        case 'range':
          return 'lang.investmentOpportunities.historicalGains';
        case 'minOnly':
          return 'lang.investmentOpportunities.historicalGainsMinOnly';
        case 'maxOnly':
          return 'lang.investmentOpportunities.historicalGainsMaxOnly';
        default:
          return this.assertUnreachable(yieldDisplay.mode);
      }
    }

    switch (yieldDisplay.mode) {
      case 'single':
        return 'lang.investmentOpportunities.targetYieldSingle';
      case 'range':
        return 'lang.investmentOpportunities.targetYield';
      case 'minOnly':
        return 'lang.investmentOpportunities.targetYieldMinOnly';
      case 'maxOnly':
        return 'lang.investmentOpportunities.targetYieldMaxOnly';
      default:
        return this.assertUnreachable(yieldDisplay.mode);
    }
  }

  private getOpportunityYieldI18nParams(
    yieldDisplay: OpportunityYieldDisplay,
  ): Record<string, string> {
    switch (yieldDisplay.mode) {
      case 'single':
        return { value: this.formatPercentage(yieldDisplay.value!) };
      case 'range':
        return {
          min: this.formatPercentage(yieldDisplay.min!),
          max: this.formatPercentage(yieldDisplay.max!),
        };
      case 'minOnly':
        return { min: this.formatPercentage(yieldDisplay.min!) };
      case 'maxOnly':
        return { max: this.formatPercentage(yieldDisplay.max!) };
      default:
        return this.assertUnreachable(yieldDisplay.mode);
    }
  }

  private getOpportunityYieldAnalyticsDisplay(
    yieldDisplay: OpportunityYieldDisplay,
  ): string {
    switch (yieldDisplay.mode) {
      case 'single':
        return `${yieldDisplay.value}`;
      case 'range':
        return `${yieldDisplay.min}-${yieldDisplay.max}`;
      case 'minOnly':
        return `>=${yieldDisplay.min}`;
      case 'maxOnly':
        return `<=${yieldDisplay.max}`;
      default:
        return this.assertUnreachable(yieldDisplay.mode);
    }
  }

  private assertUnreachable(value: OpportunityYieldMode): never {
    throw new Error(`Unhandled yield mode: ${value}`);
  }

  closePromoBanner(trackDismiss = true) {
    if (!this.showPromoBanner()) {
      return;
    }

    if (trackDismiss) {
      this.analyticsService.captureWithContext(
        'investor_landing_banner_promo_dismiss',
        {
          section: 'promo_banner',
          language: this.languageService.languageState(),
          promo: true,
        },
      );
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { promo: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  handlePromoCta() {
    this.trackPromoBannerClick('cta');
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { promo: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.openExternalUrl('https://mzic.app.link/test');
  }

  private trackPromoBannerClick(target: 'cta') {
    this.analyticsService.captureWithContext(
      'investor_landing_banner_promo_click',
      {
        section: 'promo_banner',
        language: this.languageService.languageState(),
        promo: true,
        target,
      },
    );
  }

  private openExternalUrl(url: string) {
    window.open(url, '_blank', 'noopener');
  }
}
